import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { DomainListItem } from '@app/core/interfaces/c5/domain-list-item';
import { EnhancedAllowedDevice } from '@app/core/interfaces/enhanced-allowed-device';
import { EnterpriseOptionDefinition } from '@app/core/interfaces/enterprise-option-definition';
import { FeatureProfile } from '@app/core/interfaces/feature-profile';
import { SystemFiles } from '@app/core/interfaces/portal/system-files';
import { ProductOption } from '@app/core/interfaces/product-option';
import { ResellerSBC } from '@app/core/interfaces/sbc';
import { SecurityPermissionName } from '@app/core/models/permission';
import { AllowedDeviceRepository } from '@app/core/repositories/allowed-device.repository';
import { DeviceManufacturerRepository } from '@app/core/repositories/device-manufacturer.repository';
import { EnterpriseOptionRepository } from '@app/core/repositories/enterprise-option.repository';
import { EnterpriseRepository } from '@app/core/repositories/enterprise.repository';
import { FeatureProfileRepository } from '@app/core/repositories/feature-profile.repository';
import { FileRepository } from '@app/core/repositories/file.repository';
import { ProductOptionRepository } from '@app/core/repositories/product-option.repository';
import { SBCRepository } from '@app/core/repositories/sbc.repository';
import { ApplicationEventService, EnterpriseChanged } from '@app/core/services/application-events';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { cached, Cached } from '@app/utils/cached';
import { wrapError } from '@app/utils/rxjs';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ServiceProviderContextService {
  private noCache: boolean = false;

  private _allowedDevices: Cached<EnhancedAllowedDevice[]>;

  get allowedDevices(): Cached<EnhancedAllowedDevice[]> {
    this._allowedDevices ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.allowedDeviceRepository.list(),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:allowed-devices`,
      routeParameters: ['serviceProviderId'],
    });

    return this._allowedDevices;
  }

  private _enterprises: Cached<DomainListItem[]> | null = null;

  get enterprises(): Cached<DomainListItem[]> {
    this._enterprises ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.enterpriseRepository.listEnterprises(this.noCache).pipe(
          tap(() => {
            this.noCache = false;
          }),
          wrapError('service-provider-context-service:enterprises')
        ),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:enterprises`,
      routeParameters: ['serviceProviderId'],
    });

    return this._enterprises;
  }

  private _enterpriseOptionDefinitions: Cached<EnterpriseOptionDefinition[]> | null = null;

  get enterpriseOptionDefinitions(): Cached<EnterpriseOptionDefinition[]> {
    this._enterpriseOptionDefinitions ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.enterpriseOptionRepository.listDefinitions(),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:enterprise-option-definitions`,
      routeParameters: ['serviceProviderId'],
    });

    return this._enterpriseOptionDefinitions;
  }

  private _featureProfiles: Cached<FeatureProfile[]> | null = null;

  get featureProfiles(): Cached<FeatureProfile[]> {
    this._featureProfiles ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () =>
        this.featureProfileRepository
          .listResellerFeatureProfiles()
          .pipe(wrapError('service-provider-context-service:feature-profiles')),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:feature-profiles`,
      routeParameters: ['serviceProviderId'],
    });

    return this._featureProfiles;
  }

  private _resellerSBCs: Cached<ResellerSBC[]> | null = null;

  get resellerSBCs(): Cached<ResellerSBC[]> {
    this._resellerSBCs ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        if (
          this.authenticationService.userIsEndUser() ||
          this.authenticationService.hasPermission(SecurityPermissionName.VIEW_SBC)
        ) {
          return this.sbcRepository
            .listResellerSBCs()
            .pipe(wrapError('service-provider-context-service:resellerSBCs'));
        } else {
          return of([]);
        }
      },
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:SBCs`,
      routeParameters: ['serviceProviderId'],
    });

    return this._resellerSBCs;
  }

  private _files: Cached<SystemFiles> | null = null;

  get files(): Cached<SystemFiles> {
    this._files ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => this.fileRepository.listSystemFiles(),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:files`,
      routeParameters: ['serviceProviderId'],
    });

    return this._files;
  }

  private _productOptions: Cached<ProductOption[]> | null = null;

  get productOptions(): Cached<ProductOption[]> {
    this._productOptions ??= cached({
      dataProducer: () => {
        if (this.authenticationService.hasPermission(SecurityPermissionName.VIEW_PRODUCT_OPTION)) {
          return this.productOptionRepository
            .listProductOptions()
            .pipe(wrapError('service-provider-context-service:product-options'));
        } else {
          return of([]);
        }
      },
      id: 'service-provider-context:product-options',
    });

    return this._productOptions;
  }

  get serviceProvider$(): Observable<SharedServiceProviderData> {
    return this.authenticationService.authUser$.pipe(
      map((user) => {
        return {
          id: user.serviceProviderId,
          name: user.serviceProviderName,
        };
      })
    );
  }

  private _featureProfile: Record<number, Cached<FeatureProfile>> = {};

  featureProfileById(id: number): Cached<FeatureProfile> {
    if (this._featureProfile[id] == null) {
      this._featureProfile[id] = cached({
        dataProducer: () =>
          this.featureProfileRepository
            .getResellerFeatureProfileById(id)
            .pipe(wrapError(`service-provider-context-service:feature-profile-by-id-${id}`)),
        id: `service-provider-context:feature-profile-${id}`,
      });
    }

    return this._featureProfile[id];
  }

  constructor(
    applicationEventService: ApplicationEventService,
    private allowedDeviceRepository: AllowedDeviceRepository,
    private authenticationService: AuthenticationService,
    private cachedHelperService: CachedHelperService,
    private deviceManufacturerRepository: DeviceManufacturerRepository,
    private enterpriseRepository: EnterpriseRepository,
    private enterpriseOptionRepository: EnterpriseOptionRepository,
    private featureProfileRepository: FeatureProfileRepository,
    private fileRepository: FileRepository,
    private productOptionRepository: ProductOptionRepository,
    private sbcRepository: SBCRepository
  ) {
    applicationEventService.on$(EnterpriseChanged).subscribe(() => {
      this._enterprises?.reload();
    });
  }
}

interface SharedServiceProviderData {
  id?: number;
  name: string;
}
